import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info, ShieldCheck, X } from 'lucide-react';
import tw, { styled } from 'twin.macro';

const Backdrop = styled.div`
  ${tw`fixed inset-0 z-50 flex items-end justify-center bg-zinc-950/65 p-0 sm:items-center sm:p-6`}
  animation: technical-backdrop-in 160ms ease-out both;

  @keyframes technical-backdrop-in {
    from {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Surface = styled.div`
  ${tw`flex max-h-[84vh] w-full max-w-xl flex-col overflow-hidden rounded-t-xl bg-white text-zinc-950 shadow-2xl sm:rounded-xl`}
  animation: technical-dialog-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @keyframes technical-dialog-in {
    from {
      opacity: 0;
      transform: translateY(0.75rem);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default function TechnicalDetails({ onClose }) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return createPortal(
    <Backdrop
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Surface
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="technical-dialog-title"
        aria-describedby="technical-dialog-intro"
        onKeyDown={handleKeyDown}
      >
        <header tw="flex items-start justify-between gap-4 border-b border-zinc-200 px-4 py-4 sm:px-6">
          <div>
            <h2
              id="technical-dialog-title"
              tw="flex items-center gap-2 text-xl font-semibold tracking-[-0.02em] sm:text-2xl"
            >
              <Info size={19} aria-hidden="true" tw="shrink-0 text-indigo-600" />
              Varför lämnar vi den gamla sidan?
            </h2>
            <p id="technical-dialog-intro" tw="mt-1 text-xs leading-5 text-zinc-600 sm:text-sm">
              Din portfölj ligger tryggt kvar. Här förklarar vi varför tjänsten behöver flytta och
              hur den äldre tekniska lösningen påverkar underhållet.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Stäng teknisk information"
            tw="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-700 transition hover:bg-zinc-200 hover:text-zinc-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>

        <div tw="overflow-y-auto px-4 py-1 sm:px-6">
          <section tw="border-b border-zinc-200 py-3.5">
            <h3 tw="text-sm font-semibold text-zinc-950">Datan finns bara i din webbläsare</h3>
            <p tw="mt-1 text-xs leading-5 text-zinc-600 sm:text-sm sm:leading-6">
              Den här versionen sparar portföljen som lokal webbläsardata på varje enhet. Vi kan
              därför inte läsa eller flytta den centralt åt dig; flytten måste startas i just den
              webbläsare där datan finns.
            </p>
          </section>

          <section tw="border-b border-zinc-200 py-3.5">
            <h3 tw="text-sm font-semibold text-zinc-950">En separat, äldre datakedja</h3>
            <p tw="mt-1 text-xs leading-5 text-zinc-600 sm:text-sm sm:leading-6">
              Den gamla appen bygger på en instrumentlista som uppdateras manuellt och på en separat
              Vercel-lösning som hämtar marknadsdata. Om även den här adressen ska vara i drift
              behöver vi underhålla flera parallella lösningar för samma data.
            </p>
            <p tw="mt-2 text-xs leading-5 text-zinc-600 sm:text-sm sm:leading-6">
              För att kunna driva det välbekanta upplägget vidare med fungerande uppdateringar
              behövde vi flytta det till den nya domänen{' '}
              <strong tw="font-semibold text-zinc-900">legacy.aktieutdelningar.com</strong>.
            </p>
          </section>

          <section tw="py-3.5">
            <h3 tw="text-sm font-semibold text-zinc-950">Därför flyttar vi vidare</h3>
            <p tw="mt-1 text-xs leading-5 text-zinc-600 sm:text-sm sm:leading-6">
              De två nya adresserna är versionerna vi kan hålla uppdaterade. Därför visar den gamla
              adressen nu din lokala data och hjälper dig flytta, i stället för att visa
              marknadsuppgifter som kan bli inaktuella.
            </p>
          </section>

          <p tw="mb-4 flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs font-medium leading-5 text-emerald-900 sm:text-sm">
            <ShieldCheck size={17} aria-hidden="true" tw="mt-0.5 shrink-0 text-emerald-700" />
            Inget av detta ändrar din sparade portfölj. Den ligger kvar på den här enheten även
            efter en flytt.
          </p>
        </div>
      </Surface>
    </Backdrop>,
    document.body
  );
}
